import { RiArrowLeftLine, RiCodeSSlashLine, RiLockLine, RiTimerLine, RiBookOpenLine, RiExternalLinkLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/seo/SEO';
import { Card } from '@/components/ui';
import styles from './ApiDocs.module.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const ENDPOINTS = [
    {
        group: 'Autenticacion',
        items: [
            { method: 'POST', path: '/auth/login/', desc: 'Iniciar sesion con email y password' },
            { method: 'POST', path: '/auth/registration/', desc: 'Registrar nuevo usuario' },
            { method: 'POST', path: '/auth/token/refresh/', desc: 'Renovar access token' },
            { method: 'POST', path: '/auth/google/', desc: 'Login con Google OAuth' },
            { method: 'POST', path: '/auth/password/reset/', desc: 'Solicitar reset de password' },
            { method: 'POST', path: '/auth/password/change/', desc: 'Cambiar password (autenticado)' },
        ],
    },
    {
        group: 'Worker',
        items: [
            { method: 'GET', path: '/api/v1/worker/me/', desc: 'Obtener perfil del worker' },
            { method: 'PATCH', path: '/api/v1/worker/me/', desc: 'Actualizar perfil del worker' },
            { method: 'GET', path: '/api/v1/worker/feed/', desc: 'Feed de trabajos disponibles' },
            { method: 'GET', path: '/api/v1/worker/feed/:id/', desc: 'Detalle de un trabajo' },
            { method: 'POST', path: '/api/v1/worker/feed/:id/claim/', desc: 'Reclamar un trabajo' },
            { method: 'GET', path: '/api/v1/worker/assignments/', desc: 'Listar asignaciones' },
            { method: 'GET', path: '/api/v1/worker/assignments/:id/', desc: 'Detalle de asignacion' },
            { method: 'POST', path: '/api/v1/worker/assignments/:id/submit/', desc: 'Enviar resultado' },
            { method: 'GET', path: '/api/v1/worker/transactions/', desc: 'Historial de transacciones' },
        ],
    },
    {
        group: 'Profesiones',
        items: [
            { method: 'GET', path: '/api/v1/professions/', desc: 'Listar profesiones' },
        ],
    },
];

function MethodBadge({ method }: { method: string }) {
    const colorMap: Record<string, string> = {
        GET: styles.methodGet,
        POST: styles.methodPost,
        PATCH: styles.methodPatch,
        DELETE: styles.methodDelete,
    };
    return <span className={`${styles.method} ${colorMap[method] || ''}`}>{method}</span>;
}

export function ApiDocs() {
    const navigate = useNavigate();

    return (
        <div className={styles.container}>
            <SEO
                title="Documentacion API"
                description="Documentacion completa de la API REST de Chamba. Endpoints, autenticacion JWT, rate limits y ejemplos."
                path="/docs/api"
            />

            <button className={styles.back} onClick={() => navigate(-1)}>
                <RiArrowLeftLine />
                Volver
            </button>

            <div className={styles.hero}>
                <RiCodeSSlashLine className={styles.heroIcon} />
                <h1 className={styles.heroTitle}>API de Chamba</h1>
                <p className={styles.heroDesc}>
                    REST API con autenticacion JWT, paginacion LimitOffset y documentacion OpenAPI 3.0.
                </p>
                <div className={styles.heroLinks}>
                    <a href={`${API_BASE}/api/docs/`} target="_blank" rel="noopener noreferrer" className={styles.heroLink}>
                        <RiBookOpenLine /> Swagger UI <RiExternalLinkLine />
                    </a>
                    <a href={`${API_BASE}/api/redoc/`} target="_blank" rel="noopener noreferrer" className={styles.heroLink}>
                        <RiBookOpenLine /> ReDoc <RiExternalLinkLine />
                    </a>
                </div>
            </div>

            <div className={styles.infoCards}>
                <Card className={styles.infoCard}>
                    <RiLockLine className={styles.infoIcon} />
                    <h3 className={styles.infoTitle}>Autenticacion</h3>
                    <p className={styles.infoText}>
                        JWT Bearer tokens. Envia <code>Authorization: Bearer &lt;token&gt;</code> en cada request autenticado. Refresh automatico.
                    </p>
                </Card>
                <Card className={styles.infoCard}>
                    <RiTimerLine className={styles.infoIcon} />
                    <h3 className={styles.infoTitle}>Rate Limits</h3>
                    <p className={styles.infoText}>
                        Anonimo: 100 req/hora. Autenticado: 1,000 req/hora. Auth endpoints: 5 req/minuto.
                    </p>
                </Card>
                <Card className={styles.infoCard}>
                    <RiCodeSSlashLine className={styles.infoIcon} />
                    <h3 className={styles.infoTitle}>Paginacion</h3>
                    <p className={styles.infoText}>
                        LimitOffset: <code>?limit=10&offset=0</code>. Default 10 items, max 100. Response con count, next, previous.
                    </p>
                </Card>
            </div>

            <Card className={styles.endpointsCard}>
                <h2 className={styles.endpointsTitle}>Endpoints</h2>
                {ENDPOINTS.map((group) => (
                    <div key={group.group} className={styles.group}>
                        <h3 className={styles.groupTitle}>{group.group}</h3>
                        <div className={styles.endpointList}>
                            {group.items.map((ep) => (
                                <div key={`${ep.method}-${ep.path}`} className={styles.endpoint}>
                                    <MethodBadge method={ep.method} />
                                    <code className={styles.path}>{ep.path}</code>
                                    <span className={styles.desc}>{ep.desc}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </Card>

            <Card className={styles.exampleCard}>
                <h2 className={styles.endpointsTitle}>Ejemplo de uso</h2>
                <pre className={styles.codeBlock}>
{`# Login
curl -X POST ${API_BASE}/auth/login/ \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "secret"}'

# Response: { "access": "eyJ...", "refresh": "eyJ..." }

# Listar feed de trabajos
curl ${API_BASE}/api/v1/worker/feed/ \\
  -H "Authorization: Bearer eyJ..."

# Reclamar un trabajo
curl -X POST ${API_BASE}/api/v1/worker/feed/1/claim/ \\
  -H "Authorization: Bearer eyJ..."

# Enviar resultado
curl -X POST ${API_BASE}/api/v1/worker/assignments/1/submit/ \\
  -H "Authorization: Bearer eyJ..." \\
  -H "Content-Type: application/json" \\
  -d '{"result": {"verdict": "good", "notes": "Todo correcto"}}'`}
                </pre>
            </Card>
        </div>
    );
}
